import React from 'react';
import _ from 'lodash';

import WorkerSettingsContext from '../../services/workerSettings/WorkerSettingsContext';
import Tippy, { ToolTip } from '../../components/ReactTippy';
import { QuestionIcon } from '../../components/icons/QuestionIcon';
import { Description } from '../Description';
import { isNullOrUndefinedOrEmptyStr } from '../../utilities/stringUtil';
import {
  SORT_TYPE,
  FIELD_TREE_WORKER_CCUS_SYNCED_STATUS,
  CONNECT_WORK_HISTORY_STATUS,
  CCUS_ENGINEER_ID_STATUS,
  WORKER_SETTING_STATUS,
} from '../../utilities/constants';
import { messageUtility } from '../../utilities/messageUtility';
import Styles from '../../styles/componentStyle/workerSettings/WorkerSettings.scss';

/**
 * 作業員設定状況一覧 (CCUS連携)データテーブル
 * */
export const CcusTableLayout = () => {
  // 画面表示
  return (
    <WorkerSettingsContext.Consumer>
      {({ state, changeSort }) => (
        <div className={`${Styles.ccusTable} master-slide-view__body`}>
          {state.ccusSecededWarning && (
            <Description type='primary'>
              <span className='text--negative'>
                {messageUtility.getMessage(
                  state.jvFlag ? 'I-002-013-02' : 'I-002-013-06'
                )}
              </span>
            </Description>
          )}
          <table className='table'>
            <thead className='table__header'>
              <tr className='table__label'>
                {data.tableTitles.map((title, index) => (
                  <TableHead
                    title={title}
                    key={index}
                    searchForm={state.searchForm}
                    changeSort={changeSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody className='table__body'>
              {state.workerList.map((item, index) => (
                <FixItems worker={item} key={index} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WorkerSettingsContext.Consumer>
  );
};

const TableHead = ({ title, changeSort, searchForm }) => {
  const handleSorting = () => {
    if (title.doSort) {
      changeSort(title.name);
    }
  };

  const renderSortIcon = () => {
    if (searchForm.order !== title.name) {
      if (title.doSort) {
        return (
          <button className='btn-icon--xs--dark btn-icon__sort btn-icon__sort--low-opacity'>
            <i className='i-sortdefault i-1x' />
          </button>
        );
      }
      return null;
    }

    if (searchForm.sortType === SORT_TYPE.asc.id) {
      return (
        <button className='btn-icon--xs--dark'>
          <i className='i-sortaz i-1x' />
        </button>
      );
    }

    if (searchForm.sortType === SORT_TYPE.desc.id) {
      return (
        <button className='btn-icon--xs--dark'>
          <i className='i-sortza i-1x' />
        </button>
      );
    }
  };

  return (
    <th
      className='value-table value-table--table__label--with-tooltip'
      onClick={handleSorting}
    >
      {title.tooltip ? (
        <Tippy
          duration={10}
          distance={24}
          arrowType='sharp'
          delay={[500, 0]}
          placement='bottom'
          followCursor={true}
          html={<div className='tippy-tooltip'>{title.tooltip}</div>}
        >
          <div className='value-table__label'>
            <span className='value-table__label-text'>{title.label}</span>

            <QuestionIcon className='icon--1_6x question-icon' />
          </div>
        </Tippy>
      ) : (
        <div className='value-table__label'>
          <span
            className={`${
              title.doSort !== true
                ? 'value-table__label-no-sort-icon'
                : 'value-table__label-with-sort-icon'
            }`}
          >
            {title.label}
            {renderSortIcon()}
          </span>
        </div>
      )}
    </th>
  );
};

/**
 * 作業員設定状況一覧 (CCUS連携)データ表示
 * */
const FixItems = ({ worker }) => {
  const getLabelById = (labels, id) =>
    _.find(labels, item => item.ID === id) || {};
  const valueToNumericBoolean = value => Number(Boolean(value));
  // BLDE_V2-50226 CCUS事業者IDは削除されたら、就業履歴送信を「送信不可」に変更する
  const getWorkHistoryById = (labels, id) =>
    worker.field_tree_worker.ccus_jigyousya_deleted_flag
      ? getLabelById(labels, CONNECT_WORK_HISTORY_STATUS.CANNOT_CONNECT.ID)
      : getLabelById(labels, id);
  // BLDE_V2-50576 CCUS事業者IDは削除されたら、「連携済み」の施工体制連携は「連携解除」になる
  const getCcusStatusById = (labels, id) =>
    worker.field_tree_worker.ccus_jigyousya_deleted_flag &&
    worker.field_tree_worker.ccus_synced_status ===
      FIELD_TREE_WORKER_CCUS_SYNCED_STATUS.DONE.ID
      ? getLabelById(labels, FIELD_TREE_WORKER_CCUS_SYNCED_STATUS.RELEASED.ID)
      : getLabelById(labels, id);

  const { NAME: workerStatus, CLASS_NAME: workerStatusClass } = getLabelById(
    WORKER_SETTING_STATUS,
    worker.worker_status
  );
  const {
    NAME: ccusEngineerId,
    CLASS_NAME: ccusEngineerIdClass,
  } = getLabelById(
    CCUS_ENGINEER_ID_STATUS,
    valueToNumericBoolean(worker.ccus_engineer_id)
  );
  const {
    NAME: workHistories,
    CLASS_NAME: workHistoriesClass,
  } = getWorkHistoryById(
    CONNECT_WORK_HISTORY_STATUS,
    worker.ccus_check_in_out_log_sendable
  );
  const {
    NAME: ccusSyncedStatus,
    CLASS_NAME: ccusSyncedStatusClass,
  } = getCcusStatusById(
    FIELD_TREE_WORKER_CCUS_SYNCED_STATUS,
    worker.field_tree_worker.ccus_synced_status
  );

  const getCcusSyncedStatusInfo = () => {
    if (isNullOrUndefinedOrEmptyStr(worker.field_tree_worker.error_message)) {
      return <p>ー</p>;
    }
    const infoTextClassName = `value-table__ccus_connection ${
      worker.field_tree_worker.ccus_synced_status === 3 ? ' error_message' : ''
    }`;

    return (
      <WithTooltip className={infoTextClassName}>
        {worker.field_tree_worker.error_message}
      </WithTooltip>
    );
  };

  // 画面表示
  return (
    <WorkerSettingsContext.Consumer>
      {({ state }) => (
        <tr className='table__item'>
          {/*/!*所属会社*!/*/}
          <td className='gate-list-value-table__name'>
            <div className='gate-list-value-table__name__content'>
              {worker.partner_ccus_seceded_warning &&
                !state.ccusSecededWarning && (
                  <Tippy
                    duration={10}
                    distance={24}
                    delay={[50, 100]}
                    placement='bottom'
                    followCursor={true}
                    html={
                      <ToolTip tooltipStyle={{ whiteSpace: 'pre-wrap' }}>
                        {messageUtility.getMessage('I-002-013-04')}
                      </ToolTip>
                    }
                  >
                    <i className='icon-warning_round' />
                  </Tippy>
                )}
              <WithTooltip>{worker.company_name}</WithTooltip>
            </div>
          </td>
          {/*/!*一次協力会社*!/*/}
          <td className='gate-list-value-table__name'>
            <WithTooltip>{worker.primary_company_name}</WithTooltip>
          </td>
          {/*/!*作業員名*!/*/}
          <td className='gate-list-value-table__name value-table__worker-name'>
            <WithTooltip>
              <span>
                <span className='fullname-kana'>{worker.fullname_kana}</span>{' '}
                <br />
                <span className='name-kanji'>{worker.fullname}</span>
              </span>
            </WithTooltip>
          </td>
          {/*/!*作業員ステータス*!/*/}
          <td className='gate-list-value-table__name'>
            <p className={workerStatusClass}>{workerStatus}</p>
          </td>
          {/*/!*CCUS技能者ID!/*/}
          <td className='gate-list-value-table__name'>
            <p className={ccusEngineerIdClass}>{ccusEngineerId}</p>
          </td>
          {/* 就業履歴送信 */}
          <td className='gate-list-value-table__name'>
            <p className={workHistoriesClass}>{workHistories}</p>
          </td>
          {/*/!*施工体制連携*!/*/}
          <td className='gate-list-value-table__name'>
            <p className={ccusSyncedStatusClass}>{ccusSyncedStatus}</p>
          </td>
          {/*/!*詳細情報*!/*/}
          <td className='gate-list-value-table__name'>
            {getCcusSyncedStatusInfo()}
          </td>
        </tr>
      )}
    </WorkerSettingsContext.Consumer>
  );
};

const WithTooltip = ({ children, className }) => (
  <Tippy
    duration={10}
    distance={24}
    arrowType='sharp'
    delay={[100, 0]}
    placement='bottom'
    followCursor={true}
    html={<div className={Styles.toopTip}>{children}</div>}
  >
    <p className={className}>{children}</p>
  </Tippy>
);

// 定数
const data = {
  tableTitles: [
    { name: 'company_name', label: '所属会社', doSort: true },
    { name: 'primary_company_name', label: '一次協力会社', doSort: true },
    { name: 'worker_name', label: '氏名', doSort: true },
    { name: 'worker_status', label: 'ステータス', doSort: false },
    {
      name: 'field_ccus_engineer_id_status',
      label: 'CCUS技能者ID',
      doSort: false,
      tooltip:
        '作業員のCCUS技能者IDがBuildee上に登録されているかを表示します。',
    },
    {
      name: 'work_histories',
      label: '就業履歴送信',
      doSort: false,
      tooltip: '就業履歴情報がCCUSに送信できる状態になっているかを表示します。',
    },
    {
      name: 'field_tree_worker_ccus_synced_status',
      label: '施工体制連携',
      doSort: false,
      tooltip: '施工体制情報がCCUSと連携されているかを表示します。',
    },
    { name: 'has_error', label: '詳細情報', doSort: false },
  ],
};



// WEBPACK FOOTER //
// ./src/components/tables/WorkerSettingsCcusTable.jsx